import { inRange, type DateRange } from '../ledger/counting';
import type { CravingLike, SmokeLogLike } from './types';

export const TRIGGERS = [
  'coffee',
  'stress',
  'after_meals',
  'drinking',
  'boredom',
  'other',
] as const;
export type Trigger = (typeof TRIGGERS)[number];

export function isTrigger(v: unknown): v is Trigger {
  return typeof v === 'string' && (TRIGGERS as readonly string[]).includes(v);
}

export const TRIGGER_LABELS: Record<Trigger, string> = {
  coffee: 'Coffee',
  stress: 'Stress',
  after_meals: 'After meals',
  drinking: 'Drinking',
  boredom: 'Boredom',
  other: 'Something else',
};

/** One simple plan per trigger. The user can replace any of them with their own words. */
export const DEFAULT_TRIGGER_PLANS: Record<Trigger, string> = {
  coffee:
    "Hold the cup in the hand you smoke with, and have your coffee somewhere you don't usually smoke.",
  stress: 'Breathe in for 4 and out for 6, five times. Then decide.',
  after_meals: 'Get up from the table right away: brush your teeth or take a short walk.',
  drinking: "Keep something in your hands, and step outside with someone who doesn't smoke.",
  boredom: 'Have a 10-minute task ready: a short walk, a message to a friend, a glass of water.',
  other: 'Notice what was happening, then wait 10 minutes before deciding.',
};

export const MAX_TRIGGER_PLAN_LENGTH = 200;

export function planForTrigger(t: Trigger, userPlans: Readonly<Record<string, string>>): string {
  const own = userPlans[t]?.trim();
  return own ? own : DEFAULT_TRIGGER_PLANS[t];
}

export interface TriggerStat {
  trigger: Trigger;
  /** Cigarettes tagged with it (taps). */
  smoked: number;
  /** Cravings with it that passed without smoking. */
  resisted: number;
  total: number;
}

/** Triggers in the range, most frequent first. A craving that ended in a cigarette is counted by its tap. */
export function topTriggers(
  logs: readonly SmokeLogLike[],
  cravings: readonly CravingLike[],
  range: DateRange,
): TriggerStat[] {
  const stats = new Map<Trigger, TriggerStat>();
  const get = (t: Trigger) => {
    const s = stats.get(t) ?? { trigger: t, smoked: 0, resisted: 0, total: 0 };
    stats.set(t, s);
    return s;
  };
  for (const l of logs) {
    if (
      l.deletedAt !== null ||
      l.kind !== 'tap' ||
      !isTrigger(l.trigger) ||
      !inRange(l.date, range)
    )
      continue;
    const s = get(l.trigger);
    s.smoked += l.count;
    s.total += l.count;
  }
  for (const c of cravings) {
    if (c.deletedAt !== null || !c.resisted || !isTrigger(c.trigger) || !inRange(c.date, range))
      continue;
    const s = get(c.trigger);
    s.resisted += 1;
    s.total += 1;
  }
  return [...stats.values()].sort(
    (a, b) =>
      b.total - a.total ||
      b.smoked - a.smoked ||
      TRIGGERS.indexOf(a.trigger) - TRIGGERS.indexOf(b.trigger),
  );
}
